"use client";

import { useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from "react";
import Link from "next/link";
import { useMutation } from "convex/react";
import { ArrowLeft, CheckCircle2, FileText, Loader2, Phone, Upload } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import { analytics, getLeadAttribution } from "@/lib/analytics";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";
import { getPublicPageBrand } from "@/lib/public-brand-theme";

const roles = ["Bartender", "Dishwasher", "Line Cook", "Runner", "Server"] as const;
const acceptedFileTypes = ".pdf,.doc,.docx";
const acceptedResumeExtensions = new Set(["pdf", "doc", "docx"]);
const acceptedResumeMimeTypes = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);
const maxResumeSizeMb = 10;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const saffronInputClass =
  "h-[50px] w-full rounded-[10px] border border-[#6d6d6d] bg-white px-4 text-[16px] leading-none text-[#1f1f1f] shadow-none outline-none transition-[border-color,box-shadow] placeholder:text-[#777] focus-visible:border-[var(--roma-terracotta)] focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15 disabled:cursor-not-allowed disabled:opacity-60";

const saffronTextareaClass =
  "min-h-[132px] w-full resize-none rounded-[10px] border border-[#6d6d6d] bg-white px-4 py-3 text-[16px] leading-6 text-[#1f1f1f] shadow-none outline-none transition-[border-color,box-shadow] placeholder:text-[#777] focus-visible:border-[var(--roma-terracotta)] focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15 disabled:cursor-not-allowed disabled:opacity-60";

type JobsFormData = {
  website: string;
  name: string;
  phone: string;
  email: string;
  age: string;
  experience: string;
  coverLetter: string;
};

const initialFormData: JobsFormData = {
  website: "",
  name: "",
  phone: "",
  email: "",
  age: "",
  experience: "",
  coverLetter: "",
};

export default function JobsPage() {
  const generateUploadUrl = useMutation(api.candidates.generateUploadUrl);
  const submitCandidate = useMutation(api.candidates.submit);
  const {
    location,
    restaurantCopy,
    siteConfig,
    isDefaultLocation,
    romaFunnelEnabled,
  } =
    useMarketingLocationContext();
  const pageBrand = getPublicPageBrand(siteConfig);
  const isRomaDarkFunnel = pageBrand === "chefs-roma" && romaFunnelEnabled;
  const phoneDisplay = restaurantCopy.phoneDisplay;
  const phoneHref = restaurantCopy.phoneHref;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [formData, setFormData] = useState<JobsFormData>(initialFormData);

  const updateFormData = (field: keyof JobsFormData, value: string) => {
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setSelectedFile(null);
    setSelectedRoles([]);
    setFormData(initialFormData);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const startAnotherApplication = () => {
    resetForm();
    setSubmittedName("");
    setIsSubmitted(false);
  };

  const handleRoleToggle = (role: string) => {
    setSelectedRoles((current) =>
      current.includes(role)
        ? current.filter((item) => item !== role)
        : [...current, role],
    );
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
    const isAcceptedResume =
      acceptedResumeExtensions.has(extension) ||
      (file.type ? acceptedResumeMimeTypes.has(file.type) : false);

    if (!isAcceptedResume) {
      toast.error("Please upload a PDF, DOC, or DOCX resume.");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (file.size > maxResumeSizeMb * 1024 * 1024) {
      toast.error(`File too large. Maximum size is ${maxResumeSizeMb}MB.`);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setSelectedFile(file);
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (formData.website.trim()) return;

    const trimmed = {
      name: formData.name.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      age: formData.age.trim(),
      experience: formData.experience.trim(),
      coverLetter: formData.coverLetter.trim(),
    };

    const parsedAge = Number.parseInt(trimmed.age, 10);
    if (
      !trimmed.age ||
      !Number.isInteger(parsedAge) ||
      parsedAge < 16 ||
      parsedAge > 99
    ) {
      toast.error("Enter an age between 16 and 99.");
      return;
    }

    if (!trimmed.name || !trimmed.phone || !trimmed.email || !trimmed.experience) {
      toast.error("Please fill out every required field.");
      return;
    }

    if (!emailPattern.test(trimmed.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);

    try {
      const rolePreference = selectedRoles.length
        ? selectedRoles.join(", ")
        : undefined;
      const notes = [
        rolePreference ? `Role preference: ${rolePreference}` : "Role preference: open to available roles",
        `Age: ${parsedAge}`,
        `Experience: ${trimmed.experience}`,
        trimmed.coverLetter ? `Cover letter: ${trimmed.coverLetter}` : null,
        selectedFile ? `Resume file: ${selectedFile.name}` : "Resume file: not provided",
        "Candidate follow-up: email only; no SMS consent requested.",
      ]
        .filter(Boolean)
        .join("\n\n");

      const uploadFile =
        selectedFile ??
        new File(
          [
            [
              `Name: ${trimmed.name}`,
              `Email: ${trimmed.email}`,
              `Phone: ${trimmed.phone}`,
              `Age: ${parsedAge}`,
              rolePreference ? `Applying for: ${rolePreference}` : null,
              `Experience: ${trimmed.experience}`,
              trimmed.coverLetter ? `Cover letter: ${trimmed.coverLetter}` : null,
              "Resume was optional and was not provided.",
            ]
              .filter(Boolean)
              .join("\n"),
          ],
          "no-resume-provided.txt",
          { type: "text/plain" },
        );

      const uploadUrl = await generateUploadUrl();
      const uploadResponse = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": uploadFile.type || "application/octet-stream" },
        body: uploadFile,
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload application file");
      }

      const uploaded = await uploadResponse.json();
      const storageId = uploaded.storageId as Id<"_storage">;

      const candidateId = await submitCandidate({
        name: trimmed.name,
        email: trimmed.email,
        phone: trimmed.phone,
        age: parsedAge,
        rolePreference,
        note: notes,
        storageId,
        fileName: uploadFile.name,
        attribution: getLeadAttribution("jobs_application_form"),
        locationId: location.id,
      });

      setSubmittedName(trimmed.name);
      const leadProperties = {
        lead_id: candidateId,
        lead_type: "candidate",
        source: "jobs_application_form",
      };
      analytics.formSubmitSucceeded(leadProperties);
      analytics.leadCaptured("candidate", "jobs_application_form", leadProperties);
      resetForm();
      setIsSubmitted(true);
      toast.success("Application submitted. We will follow up by email.");
    } catch (error) {
      console.error("Job application submit error:", error);
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    const firstName = submittedName.split(/\s+/)[0] || "";

    return (
      <main
        data-public-page-brand={pageBrand}
        data-roma-funnel={isRomaDarkFunnel ? "on" : undefined}
        data-roma-lead-page={isRomaDarkFunnel ? "dark" : undefined}
        className="roma-funnel-lead-page min-h-[80dvh] w-full overflow-x-hidden bg-white px-4 py-14 text-[#1f1f1f] sm:px-6"
      >
        <section className="mx-auto w-full max-w-[560px] rounded-[22px] border border-[#e3e3e3] bg-white px-6 py-10 text-center shadow-[0_20px_60px_-46px_rgba(0,0,0,0.45)]">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <p className="mt-6 text-[12px] font-bold uppercase tracking-[0.08em] text-[var(--roma-terracotta)]">
            Application received
          </p>
          <h1 className="mt-2 font-serif text-[26px] font-semibold leading-8 text-[#151515]">
            Thanks{firstName ? `, ${firstName}` : ""}. We have your application.
          </h1>
          <p className="mt-3 text-[15px] leading-7 text-[#555]">
            The {restaurantCopy.shortName} team will review your experience and role preference.
            If it looks like a fit, follow-up for this application will come by
            email.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              className="h-12 rounded-[10px] border border-[#d6d6d6] bg-white px-5 text-[15px] font-semibold text-[#1f1f1f] transition-[background-color,transform] hover:bg-[#f7f7f7] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15 active:scale-[0.98]"
              onClick={startAnotherApplication}
            >
              Submit another application
            </button>
            <a
              href={`tel:${phoneHref}`}
              className="inline-flex h-12 items-center justify-center rounded-[10px] bg-[var(--roma-terracotta)] px-5 text-[15px] font-semibold text-[#0d0d0d] transition-[background-color,transform] hover:bg-[#d94e1a] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/25 active:scale-[0.98]"
            >
              Call {phoneDisplay}
              <Phone className="ml-2 h-4 w-4" />
            </a>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main
      data-public-page-brand={pageBrand}
      data-roma-funnel={isRomaDarkFunnel ? "on" : undefined}
      data-roma-lead-page={isRomaDarkFunnel ? "dark" : undefined}
      className="roma-funnel-lead-page min-h-screen w-full overflow-x-hidden bg-white text-[#1f1f1f]"
    >
      <section className="mx-auto w-full max-w-[720px] px-4 pb-8 pt-6 sm:px-6 md:pt-8">
        <Link
          href="/"
          className="inline-flex h-10 items-center gap-2 rounded-[10px] border border-[#d8d8d8] bg-white px-3 text-[14px] font-semibold text-[#303030] transition-colors hover:bg-[#f7f7f7] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Link>

        <p className="mt-8 text-[13px] font-semibold uppercase tracking-[0.08em] text-[var(--roma-terracotta)]">
          Careers
        </p>
        <h1 className="mt-2 font-serif text-[28px] font-semibold leading-9 text-[#151515] sm:text-[32px] sm:leading-10">
          {restaurantCopy.jobsTitle}
        </h1>
        <p className="mt-4 max-w-[440px] text-[16px] leading-[1.55] text-[#202020]">
          {restaurantCopy.jobsIntro}{" "}
          Current role interests include bartender, dishwasher, line cook,
          runner, and server.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {roles.map((role) => (
            <span
              key={role}
              className="rounded-full border border-[#d8d8d8] bg-white px-3 py-1.5 text-[13px] font-semibold text-[#303030]"
            >
              {role}
            </span>
          ))}
        </div>
      </section>

      <section className="mx-auto w-full max-w-[720px] px-4 pb-10 sm:px-6">
        <div className="border-t border-[#e5e5e5] pt-8">
          <h2 className="font-serif text-[22px] font-semibold leading-8 text-[#151515]">
            Why work at {siteConfig.name}
          </h2>
          {isDefaultLocation ? (
            <p className="mt-3 text-[15px] leading-7 text-[#444]">
              Tuscan Cove Bar &amp; Patio is a neighborhood bar and patio in
              Southern Highlands, Las Vegas. Our kitchen runs on stone-fired
              pizzas, smash burgers, and a weekend brunch that keeps the patio
              busy, while the bar pours craft cocktails and a daily happy hour
              from 4 to 6 PM. The space was built for regulars, with fire pits on
              the patio and a steady local crowd that comes back week after week.
              We hire people who want a real spot to grow, not just a shift to
              fill.
            </p>
          ) : (
            <p className="mt-3 text-[15px] leading-7 text-[#444]">
              {siteConfig.name} is a neighborhood spot built for regulars, with a
              kitchen and bar that stay busy and a steady local crowd that comes
              back week after week. We hire people who want a real spot to grow,
              not just a shift to fill.
            </p>
          )}
          <p className="mt-4 text-[15px] leading-7 text-[#444]">
            {isDefaultLocation
              ? "We are a single location, so the team is tight knit and your work is visible."
              : "We run two neighborhood locations, so the team is tight knit and your work is visible."}{" "}
            There is no corporate shuffle here. You learn the menu, you learn the
            regulars, and you get to take real ownership of the guest experience.
            Whether you are early in your hospitality career or you have spent
            years behind a bar or on a line, there is room to sharpen your craft
            and pick up new stations.
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[720px] px-4 pb-10 sm:px-6">
        <h2 className="font-serif text-[22px] font-semibold leading-8 text-[#151515]">
          Roles we hire for
        </h2>
        <p className="mt-3 text-[15px] leading-7 text-[#444]">
          As a neighborhood bar and patio, we staff across the front and back of
          house. Common openings include the roles below, and we are always glad
          to hear from strong candidates even when a specific position is not
          posted.
        </p>
        <ul className="mt-4 space-y-3 text-[15px] leading-7 text-[#444]">
          <li>
            <span className="font-semibold text-[#1f1f1f]">Bartenders</span>{" "}
            {isDefaultLocation
              ? "who can build our craft cocktails, run a smooth happy hour, and keep the bar feeling like the heart of the room."
              : "who can build our craft cocktails and keep the bar feeling like the heart of the room."}
          </li>
          <li>
            <span className="font-semibold text-[#1f1f1f]">Servers</span> who
            know the food and drink menus, read a table well, and make patio
            regulars feel at home.
          </li>
          <li>
            <span className="font-semibold text-[#1f1f1f]">Line cooks</span>{" "}
            {isDefaultLocation
              ? "who want to work the stone-fired pizza oven, the smash burger flat top, and a busy brunch service."
              : "who want to work a busy line, learn every station, and keep service moving during a full house."}
          </li>
          <li>
            <span className="font-semibold text-[#1f1f1f]">Runners</span> who
            keep food moving from the kitchen to the patio quickly and
            accurately during a full house.
          </li>
          <li>
            <span className="font-semibold text-[#1f1f1f]">Dishwashers</span>{" "}
            who keep the back of house clean, stocked, and ready so every
            station can stay on pace.
          </li>
        </ul>
      </section>

      <section className="mx-auto w-full max-w-[720px] px-4 pb-10 sm:px-6">
        <h2 className="font-serif text-[22px] font-semibold leading-8 text-[#151515]">
          What we look for
        </h2>
        <p className="mt-3 text-[15px] leading-7 text-[#444]">
          The best fit for our team shows up ready, takes pride in the details,
          and treats teammates and guests with the same respect. You do not need
          a long resume to apply, but a positive attitude and a willingness to
          learn go a long way. We value reliability, clear communication, and
          people who pitch in when the patio fills up. Bar and restaurant
          experience helps, and so does any food handler or alcohol service
          certification you already hold.
        </p>
      </section>

      <section className="mx-auto w-full max-w-[720px] px-4 pb-10 sm:px-6">
        <h2 className="font-serif text-[22px] font-semibold leading-8 text-[#151515]">
          Location and getting here
        </h2>
        <p className="mt-3 text-[15px] leading-7 text-[#444]">
          You will find us at {siteConfig.address.line1},{" "}
          {siteConfig.address.city}, {siteConfig.address.state}{" "}
          {siteConfig.address.zip}
          {isDefaultLocation
            ? ", right in the Southern Highlands community on the south end of the valley. The location is an easy drive for anyone living in or near Southern Highlands, with parking on site and a short commute from the surrounding neighborhoods."
            : ". The location is an easy drive from the surrounding neighborhoods, with parking on site and a short commute from nearby areas."}{" "}
          If you have a question before you apply, you are welcome to call us at{" "}
          {phoneDisplay}.
        </p>
      </section>

      <section className="mx-auto w-full max-w-[720px] px-4 pb-10 sm:px-6">
        <h2 className="font-serif text-[22px] font-semibold leading-8 text-[#151515]">
          How to apply
        </h2>
        <p className="mt-3 text-[15px] leading-7 text-[#444]">
          Applying takes about two minutes. Fill out the short form below with
          your name, contact details, the roles you are interested in, and a
          quick summary of your experience. A resume is optional, so do not let a
          missing document hold you back. We review every application and follow
          up by email when there is a fit, so keep an eye on your inbox after you
          submit.
        </p>
      </section>

      <section
        id="application"
        className="mx-auto w-full max-w-[720px] scroll-mt-site-header px-4 pb-14 sm:px-6 md:pb-16"
      >
        <div className="border-t border-[#e5e5e5] pt-8">
          <p className="border-l-2 border-[var(--roma-terracotta)] pl-4 text-[15px] leading-6 text-[#444]">
            This takes about two minutes. Phone is required for the application
            record, but follow-up from this form is by email.
          </p>

          <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-7">
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={formData.website}
              onChange={(event) => updateFormData("website", event.target.value)}
              className="hidden"
            />

            <div className="grid gap-5 sm:grid-cols-2">
              <SaffronField label="Name" htmlFor="name" required>
                <input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(event) => updateFormData("name", event.target.value)}
                  autoComplete="name"
                  required
                  disabled={isSubmitting}
                  className={saffronInputClass}
                />
              </SaffronField>

              <SaffronField label="Phone" htmlFor="phone" required>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(event) => updateFormData("phone", event.target.value)}
                  autoComplete="tel"
                  required
                  disabled={isSubmitting}
                  className={saffronInputClass}
                />
              </SaffronField>
            </div>

            <SaffronField
              label="Email"
              htmlFor="email"
              required
              helper="The restaurant team will use this for application follow-up."
            >
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={(event) => updateFormData("email", event.target.value)}
                autoComplete="email"
                spellCheck={false}
                required
                disabled={isSubmitting}
                className={saffronInputClass}
              />
            </SaffronField>

            <SaffronField label="Age" htmlFor="age" required>
              <input
                id="age"
                name="age"
                type="number"
                inputMode="numeric"
                min={16}
                max={99}
                value={formData.age}
                onChange={(event) => updateFormData("age", event.target.value)}
                required
                disabled={isSubmitting}
                className={saffronInputClass}
              />
            </SaffronField>

            <fieldset className="space-y-3">
              <legend className="block text-[15px] font-normal leading-6 text-[#232323]">
                Role preference
              </legend>
              <p className="text-[13px] leading-5 text-[#666]">
                Choose any roles you want us to consider, or leave blank if you
                are open to the best fit.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {roles.map((role) => {
                  const checked = selectedRoles.includes(role);

                  return (
                    <label
                      key={role}
                      className={
                        checked
                          ? "flex min-h-[50px] cursor-pointer items-center gap-3 rounded-[10px] border border-[var(--roma-terracotta)] bg-[var(--roma-linen)] px-4 text-[15px] font-semibold text-[#1f1f1f] transition"
                          : "flex min-h-[50px] cursor-pointer items-center gap-3 rounded-[10px] border border-[#d8d8d8] bg-white px-4 text-[15px] font-semibold text-[#303030] transition hover:bg-[#f7f7f7]"
                      }
                    >
                      <input
                        type="checkbox"
                        name="rolePreference"
                        value={role}
                        checked={checked}
                        onChange={() => handleRoleToggle(role)}
                        disabled={isSubmitting}
                        className="h-4 w-4 shrink-0 rounded border-[#8c8c8c] accent-[var(--roma-terracotta)]"
                      />
                      <span className="min-w-0">{role}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <SaffronField
              label="Restaurant or bar experience"
              htmlFor="experience"
              required
              helper="A short summary is enough: recent role, years of experience, availability, or certifications."
            >
              <textarea
                id="experience"
                name="experience"
                rows={5}
                value={formData.experience}
                onChange={(event) => updateFormData("experience", event.target.value)}
                required
                disabled={isSubmitting}
                className={saffronTextareaClass}
              />
            </SaffronField>

            <SaffronField
              label="Cover letter"
              htmlFor="coverLetter"
              helper="Optional. Add anything you want the hiring team to know."
            >
              <textarea
                id="coverLetter"
                name="coverLetter"
                rows={4}
                value={formData.coverLetter}
                onChange={(event) => updateFormData("coverLetter", event.target.value)}
                disabled={isSubmitting}
                className={saffronTextareaClass}
              />
            </SaffronField>

            <div className="space-y-2">
              <label
                htmlFor="resume"
                className="block text-[15px] font-normal leading-6 text-[#232323]"
              >
                Resume
              </label>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSubmitting}
                className="flex min-h-[64px] w-full items-center justify-center gap-3 rounded-[12px] border-2 border-dashed border-[#cfcfcf] bg-white px-4 py-4 text-[15px] font-semibold text-[#303030] transition-colors hover:bg-[#f7f7f7] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {selectedFile ? (
                  <>
                    <FileText className="h-5 w-5 shrink-0 text-[var(--roma-terracotta)]" />
                    <span className="min-w-0 truncate">{selectedFile.name}</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-5 w-5 shrink-0 text-[var(--roma-terracotta)]" />
                    <span>Upload resume (optional)</span>
                  </>
                )}
              </button>
              <input
                ref={fileInputRef}
                id="resume"
                type="file"
                accept={acceptedFileTypes}
                onChange={handleFileChange}
                disabled={isSubmitting}
                className="hidden"
              />
              <div className="flex flex-col gap-2 text-[13px] leading-5 text-[#666] sm:flex-row sm:items-center sm:justify-between">
                <p>PDF, DOC, or DOCX up to {maxResumeSizeMb}MB. Resume is optional.</p>
                {selectedFile ? (
                  <button
                    type="button"
                    onClick={removeSelectedFile}
                    disabled={isSubmitting}
                    className="w-fit font-semibold text-[var(--roma-terracotta)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Remove file
                  </button>
                ) : null}
              </div>
            </div>

            <button
              type="submit"
              className="flex h-[50px] w-full items-center justify-center rounded-[9px] bg-[var(--roma-terracotta)] px-5 text-[13px] font-bold uppercase tracking-[0.02em] text-[#0d0d0d] transition-[background-color,transform] hover:bg-[#d94e1a] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/25 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Send application"
              )}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

function SaffronField({
  label,
  htmlFor,
  required = false,
  helper,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  helper?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label
        htmlFor={htmlFor}
        className="block text-[15px] font-normal leading-6 text-[#232323]"
      >
        {label}
        {required ? <span className="text-[var(--roma-terracotta)]"> *</span> : null}
      </label>
      {children}
      {helper ? <p className="text-[13px] leading-5 text-[#666]">{helper}</p> : null}
    </div>
  );
}
